import React, { useState, useMemo, useContext, useEffect } from "react";
import { injectIntl } from "react-intl";
import {
  Alert,
  Button,
  Input
} from "reactstrap";
import ReactTable from "react-table";
import "react-table/react-table.css";
import IntlMessages from "../../../helpers/IntlMessages";
import ShopeeWorkspaceContext from "./ShopeeWorkspaceContext";
import "./ShopeeInventory.scss";

import { mergeShopeeCatalogFilters } from "./shopeeCatalogFilters";
import { mappingHasSyncError } from "./shopeeMappingStatus";

const hasValue = value => value !== null && value !== undefined && value !== "";
const numberValue = value => {
  if (!hasValue(value)) return null;
  const normalized = Number(value);
  return Number.isFinite(normalized) ? normalized : null;
};
const textValue = value => {
  if (!hasValue(value)) return null;
  const normalized = String(value).trim();
  return normalized || null;
};
const formatNumber = value => {
  const normalized = numberValue(value);
  return normalized === null ? "—" : normalized.toLocaleString("vi-VN");
};
const formatTimestamp = value => {
  if (!value) return null;
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return null;
  return new Intl.DateTimeFormat("vi-VN", {
    day: "2-digit", month: "2-digit", year: "numeric", hour: "2-digit", minute: "2-digit", hour12: false
  }).format(date);
};

export const SHOPEE_INVENTORY_STATUS_LABEL_IDS = {
  unknown: "shopee.workspace.inventory.status.unknown",
  unmapped: "shopee.workspace.inventory.status.unmapped",
  error: "shopee.workspace.inventory.status.error",
  out_of_stock: "shopee.workspace.inventory.status.out-of-stock",
  variance: "shopee.workspace.inventory.status.variance",
  synced: "shopee.workspace.inventory.status.synced"
};

export const normalizeShopeeInventoryMapping = mapping => {
  const source = mapping || {};
  const vmassStock = numberValue(source.vmass_stock);
  const shopeeStock = numberValue(source.platform_stock);
  const diff = vmassStock === null || shopeeStock === null ? null : shopeeStock - vmassStock;
  let status = "unknown";
  let statusLabelId = SHOPEE_INVENTORY_STATUS_LABEL_IDS.unknown;
  let statusClass = "status-chenhlech";
  if (!source.product_id) {
    status = "unmapped";
    statusLabelId = SHOPEE_INVENTORY_STATUS_LABEL_IDS.unmapped;
  } else if (mappingHasSyncError(source)) {
    status = "error";
    statusLabelId = SHOPEE_INVENTORY_STATUS_LABEL_IDS.error;
  } else if (shopeeStock !== null && shopeeStock <= 0) {
    status = "out_of_stock";
    statusLabelId = SHOPEE_INVENTORY_STATUS_LABEL_IDS.out_of_stock;
    statusClass = "status-hethang";
  } else if (diff !== null && diff !== 0) {
    status = "variance";
    statusLabelId = SHOPEE_INVENTORY_STATUS_LABEL_IDS.variance;
  } else if (diff === 0) {
    status = "synced";
    statusLabelId = SHOPEE_INVENTORY_STATUS_LABEL_IDS.synced;
    statusClass = "status-dongbo";
  }
  return {
    ...source,
    id: source.id,
    name: textValue(source.vmass_product_name) || textValue(source.platform_name) || null,
    sku: textValue(source.vmass_sku) || textValue(source.platform_sku),
    barcode: textValue(source.vmass_barcode),
    image: textValue(source.vmass_image) || textValue(source.platform_image),
    vmass_stock: vmassStock,
    shopee_stock: shopeeStock,
    shopee_diff: diff,
    status,
    status_label_id: statusLabelId,
    status_class: statusClass,
    updated_at: source.last_synced_at || source.updatedAt || null
  };
};

export const getShopeeInventoryMetrics = (mappings, meta = {}) => {
  const rows = (Array.isArray(mappings) ? mappings : []).map(normalizeShopeeInventoryMapping);
  const count = status => rows.filter(row => row.status === status).length;
  const timestamps = rows
    .map(row => row.updated_at ? new Date(row.updated_at).getTime() : Number.NaN)
    .filter(Number.isFinite);
  const total = numberValue(meta.total);
  return {
    total: total === null ? rows.length : total,
    loadedRows: rows.length,
    synced: count("synced"),
    variance: count("variance"),
    outOfStock: count("out_of_stock"),
    unmapped: count("unmapped"),
    errors: count("error"),
    lastUpdatedAt: timestamps.length ? Math.max(...timestamps) : null
  };
};

export const getTopShopeeInventoryVariance = rows =>
  (Array.isArray(rows) ? rows : [])
    .filter(row => numberValue(row.shopee_diff) !== null && numberValue(row.shopee_diff) !== 0)
    .sort((left, right) => Math.abs(right.shopee_diff) - Math.abs(left.shopee_diff))
    .slice(0, 5);

export default injectIntl(function ShopeeInventoryPanel({ intl }) {
  const workspace = useContext(ShopeeWorkspaceContext) || {};
  const [searchTerm, setSearchTerm] = useState("");
  const [statusFilter, setStatusFilter] = useState("all");
  const rawMappings = workspace.data && workspace.data.mappings;
  const setWorkspaceFilters = workspace.setFilters;
  const mappings = useMemo(
    () => (Array.isArray(rawMappings) ? rawMappings : []).map(normalizeShopeeInventoryMapping),
    [rawMappings]
  );
  const metrics = useMemo(
    () => getShopeeInventoryMetrics(rawMappings, workspace.meta || {}),
    [rawMappings, workspace.meta]
  );

  useEffect(() => {
    if (typeof setWorkspaceFilters !== "function") return undefined;
    const timer = setTimeout(() => {
      setWorkspaceFilters(current => mergeShopeeCatalogFilters(current, { search: searchTerm }));
    }, 250);
    return () => clearTimeout(timer);
  }, [searchTerm, setWorkspaceFilters]);

  const filteredData = useMemo(
    () => statusFilter === "all" ? mappings : mappings.filter(item => item.status === statusFilter),
    [mappings, statusFilter]
  );
  const topVarianceProducts = useMemo(() => getTopShopeeInventoryVariance(mappings), [mappings]);
  const filters = workspace.filters || {};
  const pageSize = Math.max(1, numberValue(filters.page_size) || 20);
  const currentPage = Math.max(1, numberValue(filters.page) || 1);
  const totalPages = Math.max(1, Math.ceil(metrics.total / pageSize));
  const startItem = filteredData.length ? (currentPage - 1) * pageSize + 1 : 0;
  const endItem = filteredData.length ? startItem + filteredData.length - 1 : 0;
  const selectedShop = (workspace.shops || []).find(shop => String(shop.shop_id) === String(workspace.selectedShopId));
  const shopLabel = workspace.selectedShopId === "ALL"
    ? intl.formatMessage(
        { id: "shopee.workspace.sidebar.all-shops" },
        { count: (workspace.shops || []).length }
      )
    : selectedShop
      ? selectedShop.shop_name ||
        intl.formatMessage(
          { id: "shopee.workspace.overview.shop-label" },
          { shopId: selectedShop.shop_id }
        )
      : intl.formatMessage({ id: "shopee.workspace.inventory.no-shop-selected" });
  const setPage = page => {
    if (typeof setWorkspaceFilters === "function") setWorkspaceFilters(current => ({ ...current, page }));
  };
  const setPageSize = event => {
    if (typeof setWorkspaceFilters === "function") {
      setWorkspaceFilters(current => ({ ...current, page: 1, page_size: Number(event.target.value) }));
    }
  };
  const renderDiff = diff => {
    if (diff === null || diff === undefined) return <span className="diff diff-zero">—</span>;
    const className = diff < 0 ? "diff-neg" : diff > 0 ? "diff-pos" : "diff-zero";
    return <span className={`diff ${className}`}>{`${diff > 0 ? "+" : ""}${formatNumber(diff)}`}</span>;
  };
  const onPageSubtext = intl.formatMessage({
    id: "shopee.workspace.inventory.sub.on-page"
  });
  const loadedSubtext = intl.formatMessage({
    id: "shopee.workspace.inventory.sub.from-loaded-mappings"
  });
  const kpis = [
    { id: "total", icon: "simple-icon-box", colorClass: "is-blue", label: intl.formatMessage({ id: "shopee.workspace.inventory.kpi.total" }), value: formatNumber(metrics.total), subtext: intl.formatMessage({ id: "shopee.workspace.inventory.sub.by-filter" }) },
    { id: "synced", icon: "simple-icon-check", colorClass: "is-green", label: intl.formatMessage({ id: "shopee.workspace.inventory.status.synced" }), value: formatNumber(metrics.synced), subtext: intl.formatMessage({ id: "shopee.workspace.inventory.kpi.synced-sub" }, { count: formatNumber(metrics.loadedRows) }) },
    { id: "variance", icon: "simple-icon-exclamation", colorClass: "is-orange", label: intl.formatMessage({ id: "shopee.workspace.inventory.kpi.variance" }), value: formatNumber(metrics.variance), subtext: onPageSubtext },
    { id: "out_of_stock", icon: "simple-icon-close", colorClass: "is-red", label: intl.formatMessage({ id: "shopee.workspace.inventory.status.out-of-stock" }), value: formatNumber(metrics.outOfStock), subtext: onPageSubtext },
    { id: "unmapped", icon: "simple-icon-link", colorClass: "is-purple", label: intl.formatMessage({ id: "shopee.workspace.inventory.status.unmapped" }), value: formatNumber(metrics.unmapped), subtext: metrics.errors ? intl.formatMessage({ id: "shopee.workspace.inventory.kpi.errors-sub" }, { count: formatNumber(metrics.errors) }) : onPageSubtext },
    {
      id: "last_sync",
      icon: "simple-icon-clock",
      colorClass: "is-cyan",
      label: intl.formatMessage({ id: "shopee.workspace.inventory.kpi.last-sync" }),
      value: formatTimestamp(metrics.lastUpdatedAt) || intl.formatMessage({ id: "shopee.workspace.common.no-data" }),
      subtext: loadedSubtext
    }
  ];

  // ReactTable Column Definitions
  const columns = useMemo(
    () => [
      {
        Header: (
          <div>
            <IntlMessages id="shopee.workspace.catalog.col-product" />{" "}
            (<IntlMessages id="shopee.workspace.catalog.col-sku-vmass" />)
          </div>
        ),
        accessor: "name",
        minWidth: 180,
        Cell: ({ original }) => (
          <div className="shopee-inventory__product-cell">
            <div className="img-wrap">
              {original.image ? (
                <img src={original.image} alt={original.name || ""} />
              ) : (
                <div
                  className="img-placeholder"
                  aria-label={intl.formatMessage({
                    id: "shopee.workspace.inventory.img-placeholder-aria"
                  })}
                >
                  <i className="simple-icon-bag" />
                </div>
              )}
            </div>
            <div className="info">
              <span className="name" title={original.name || undefined}>
                {original.name || (
                  <IntlMessages id="shopee.workspace.inventory.no-name" />
                )}
              </span>
              <span className="code">{original.sku || "—"}</span>
              <span className="barcode">{original.barcode || "—"}</span>
            </div>
          </div>
        )
      },
      {
        Header: (
          <div>
            <IntlMessages id="shopee.workspace.inventory.col.vmass-stock" />
            <small>
              (<IntlMessages id="shopee.workspace.inventory.col.total" />)
            </small>
          </div>
        ),
        accessor: "vmass_stock",
        width: 95,
        Cell: ({ value }) => (
          <span style={{ fontWeight: 700, color: value === 0 ? "#ef4444" : "#22c55e" }}>
            {formatNumber(value)}
          </span>
        )
      },
      {
        Header: (
          <div>
            <span className="channel-mark is-shopee">S</span> Shopee
          </div>
        ),
        accessor: "shopee_stock",
        width: 90,
        Cell: ({ original }) => (
          <div className="shopee-inventory__stock-cell">
            <span className="qty">{formatNumber(original.shopee_stock)}</span>
            {renderDiff(original.shopee_diff)}
          </div>
        )
      },
      {
        Header: (
          <div>
            <IntlMessages id="shopee.workspace.inventory.status.variance" />
            <small><IntlMessages id="shopee.workspace.inventory.col.diff-formula" /></small>
          </div>
        ),
        accessor: "shopee_diff",
        width: 110,
        Cell: ({ value }) => renderDiff(value)
      },
      {
        Header: <IntlMessages id="shopee.workspace.inventory.col.condition" />,
        accessor: "status_label_id",
        width: 140,
        Cell: ({ original }) => (
          <span className={`badge-status ${original.status_class}`}>
            <IntlMessages id={original.status_label_id} />
          </span>
        )
      },
      {
        Header: (
          <div>
            <IntlMessages id="shopee.workspace.inventory.col.updated" />
            <small><IntlMessages id="shopee.workspace.inventory.col.last" /></small>
          </div>
        ),
        accessor: "updated_at",
        width: 105,
        Cell: ({ value, original }) => (
          <div style={{ fontSize: "10px", color: "#64748b", display: "flex", alignItems: "center", gap: 4 }}>
            <span
              style={{
                width: 6,
                height: 6,
                borderRadius: "50%",
                backgroundColor: original.status === "synced" ? "#22c55e" : "#f59e0b"
              }}
            />
            {formatTimestamp(value) || intl.formatMessage({ id: "shopee.workspace.common.no-data" })}
          </div>
        )
      }
    ],
    [intl]
  );

  return (
    <div className="shopee-inventory">
      <div className="shopee-inventory__header">
        <div className="title-area">
          <h2><IntlMessages id="shopee.workspace.inventory.title" /></h2>
          <p><IntlMessages id="shopee.workspace.inventory.subtitle" /></p>
        </div>
        <div className="actions-area">
          <Button color="primary" className="btn-sync" onClick={workspace.load} disabled={workspace.loading || typeof workspace.load !== "function"}>
            <i className="simple-icon-refresh" />{" "}
            <IntlMessages id="shopee.workspace.sidebar.refresh-title" />
          </Button>
          <Button color="light" outline className="btn-bulk" onClick={() => typeof workspace.setActiveTab === "function" && workspace.setActiveTab("mappings")}>
            <i className="simple-icon-link" />{" "}
            <IntlMessages id="shopee.workspace.inventory.manage-mapping" />
          </Button>
        </div>
      </div>

      {workspace.error ? <Alert color="danger">{workspace.error}</Alert> : null}

      {/* 6 Top KPI Summary Cards */}
      <div className="shopee-inventory__kpis">
        {kpis.map((kpi) => (
          <div key={kpi.id} className="shopee-inventory__kpi">
            <div className={`shopee-inventory__kpi-icon ${kpi.colorClass}`}>
              <i className={kpi.icon} />
            </div>
            <div className="shopee-inventory__kpi-info">
              <span className="label">{kpi.label}</span>
              <span className="value">{kpi.value}</span>
              <span className="subtext">{kpi.subtext}</span>
            </div>
          </div>
        ))}
      </div>

      <div className="shopee-inventory__body">
        <div className="shopee-inventory__main-col">
          <div className="shopee-inventory__toolbar">
            <div className="search-box">
              <i className="simple-icon-magnifier" />
              <Input
                type="text"
                placeholder={intl.formatMessage({
                  id: "shopee.workspace.inventory.search-placeholder"
                })}
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
              />
            </div>

            <div className="select-box">
              <Input
                type="select"
                value={statusFilter}
                onChange={(e) => setStatusFilter(e.target.value)}
              >
                <option value="all">{intl.formatMessage({ id: "shopee.workspace.inventory.filter.all" })}</option>
                <option value="synced">{intl.formatMessage({ id: "shopee.workspace.inventory.status.synced" })}</option>
                <option value="variance">{intl.formatMessage({ id: "shopee.workspace.inventory.status.variance" })}</option>
                <option value="out_of_stock">{intl.formatMessage({ id: "shopee.workspace.inventory.status.out-of-stock" })}</option>
                <option value="unmapped">{intl.formatMessage({ id: "shopee.workspace.inventory.status.unmapped" })}</option>
                <option value="error">{intl.formatMessage({ id: "shopee.workspace.inventory.status.error" })}</option>
              </Input>
            </div>

            <span className="text-muted small">
              <IntlMessages id="shopee.workspace.inventory.shop-colon" /> {shopLabel}
            </span>
          </div>

          {/* Main Card with Channel Tabs & Table Matrix */}
          <div className="shopee-inventory__card">
            <div className="shopee-inventory__table-wrapper">
              <ReactTable
                data={filteredData}
                columns={columns}
                defaultPageSize={pageSize}
                showPagination={false}
                loading={Boolean(workspace.loading)}
                noDataText={workspace.loading
                  ? intl.formatMessage({ id: "shopee.workspace.inventory.loading" })
                  : intl.formatMessage({ id: "shopee.workspace.inventory.empty" })}
                className="-striped -highlight"
                minRows={1}
              />
            </div>

            <div className="shopee-inventory__pagination">
              <span className="info-text">
                <IntlMessages
                  id="shopee.workspace.inventory.pagination-summary"
                  values={{
                    from: formatNumber(startItem),
                    to: formatNumber(endItem),
                    total: formatNumber(metrics.total)
                  }}
                />
              </span>
              <div className="controls">
                <button type="button" className="btn-page" onClick={() => setPage(currentPage - 1)} disabled={workspace.loading || currentPage <= 1} aria-label={intl.formatMessage({ id: "shopee.workspace.common.previous" })}>
                  ‹
                </button>
                <span className="btn-page active">{currentPage}</span>
                <span style={{ fontSize: 10, color: "#94a3b8" }}>
                  <IntlMessages
                    id="shopee.workspace.common.page-count"
                    values={{ total: totalPages }}
                  />
                </span>
                <button type="button" className="btn-page" onClick={() => setPage(currentPage + 1)} disabled={workspace.loading || currentPage >= totalPages} aria-label={intl.formatMessage({ id: "shopee.workspace.common.next" })}>›</button>
                <Input type="select" className="page-size-select" value={pageSize} onChange={setPageSize}>
                  {["10", "20", "50"].map(size => (
                    <option key={size} value={size}>
                      <IntlMessages
                        id="shopee.workspace.common.per-page"
                        values={{ size }}
                      />
                    </option>
                  ))}
                </Input>
              </div>
            </div>
          </div>
        </div>

        {/* Right Column: Widgets / Analytics Cards */}
        <div className="shopee-inventory__side-col">
          <div className="side-card">
            <div className="card-header-row">
              <h5><IntlMessages id="shopee.workspace.inventory.side.overview-title" /></h5>
            </div>
            <div className="legend-list p-3">
              <div className="legend-item"><span><IntlMessages id="shopee.workspace.inventory.legend.filtered" /></span><span className="val">{formatNumber(metrics.total)}</span></div>
              <div className="legend-item"><span><IntlMessages id="shopee.workspace.inventory.legend.synced-page" /></span><span className="val">{formatNumber(metrics.synced)}</span></div>
              <div className="legend-item"><span><IntlMessages id="shopee.workspace.inventory.legend.variance-page" /></span><span className="val">{formatNumber(metrics.variance)}</span></div>
              <div className="legend-item"><span><IntlMessages id="shopee.workspace.inventory.status.out-of-stock" /></span><span className="val">{formatNumber(metrics.outOfStock)}</span></div>
            </div>
          </div>

          {/* Card 2: Top Stock Variance */}
          <div className="side-card">
            <div className="card-header-row">
              <h5><IntlMessages id="shopee.workspace.inventory.side.top-variance" /></h5>
              <a href="#all" onClick={(e) => e.preventDefault()}>
                <IntlMessages id="shopee.workspace.common.view-all" />
              </a>
            </div>
            <div className="top-variance-list">
              {topVarianceProducts.length ? topVarianceProducts.map((prod, index) => (
                <div key={prod.id || `${prod.platform_item_id}-${prod.platform_model_id}`} className="variance-item">
                  <div className="left-info">
                    <span className="rank">{index + 1}</span>
                    <div className="thumb" />
                    <div className="details">
                      <span className="name">
                        {prod.name || (
                          <IntlMessages id="shopee.workspace.inventory.no-name" />
                        )}
                      </span>
                      <span className="sku">{prod.sku || "—"}</span>
                    </div>
                  </div>
                  <span className="var-value">{prod.shopee_diff > 0 ? "+" : ""}{formatNumber(prod.shopee_diff)}</span>
                </div>
              )) : (
                <div className="p-3 text-muted small">
                  <IntlMessages id="shopee.workspace.inventory.side.no-variance" />
                </div>
              )}
            </div>
          </div>

          <div className="side-card">
            <div className="card-header-row">
              <h5><IntlMessages id="shopee.workspace.inventory.side.alerts-title" /></h5>
              <span className="text-muted small"><IntlMessages id="shopee.workspace.inventory.sub.on-page" /></span>
            </div>
            <div className="alert-list">
              <div className="alert-item alert-red">
                <i className="simple-icon-exclamation icon" />
                <div className="text-wrap">
                  <span className="alert-title">
                    <IntlMessages
                      id="shopee.workspace.inventory.alert.out-of-stock"
                      values={{ count: formatNumber(metrics.outOfStock) }}
                    />
                  </span>
                  <span className="alert-desc"><IntlMessages id="shopee.workspace.inventory.sub.from-loaded-mappings" /></span>
                </div>
              </div>

              <div className="alert-item alert-orange">
                <i className="simple-icon-bell icon" />
                <div className="text-wrap">
                  <span className="alert-title">
                    <IntlMessages
                      id="shopee.workspace.inventory.alert.variance"
                      values={{ count: formatNumber(metrics.variance) }}
                    />
                  </span>
                  <span className="alert-desc"><IntlMessages id="shopee.workspace.inventory.alert.variance-desc" /></span>
                </div>
              </div>

              <div className="alert-item alert-blue">
                <i className="simple-icon-clock icon" />
                <div className="text-wrap">
                  <span className="alert-title">
                    <IntlMessages
                      id="shopee.workspace.inventory.alert.unmapped"
                      values={{ count: formatNumber(metrics.unmapped) }}
                    />
                  </span>
                  <span className="alert-desc"><IntlMessages id="shopee.workspace.inventory.alert.unmapped-desc" /></span>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
})
